import { useEffect, useState } from "react";
import { api } from "@/lib/kfmApi";
import { Loader2 } from "lucide-react";

let _cfgCache = null;
async function loadTranslations() {
  if (_cfgCache) return _cfgCache;
  const { data } = await api.get("/config");
  _cfgCache = (data.translations || []).filter((t) => t.available);
  return _cfgCache;
}

// Side-by-side comparison of the passage in the current version + a second version the
// reader chooses. Free — reuses the same /bible/passage fetch.
export default function CompareVersions({ session }) {
  const passage = session.passage || {};
  const ref = session.study?.passage_reference || passage.reference || "";
  const current = (passage.translation || "niv").toLowerCase();

  const [translations, setTranslations] = useState(_cfgCache || []);
  const [secondId, setSecondId] = useState("");
  const [second, setSecond] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadTranslations().then((list) => {
      setTranslations(list);
      const other = list.find((t) => t.id !== current);
      if (other) setSecondId(other.id);
    });
  }, [current]);

  useEffect(() => {
    if (!secondId || !ref) return;
    let cancelled = false;
    setLoading(true);
    api
      .get("/bible/passage", { params: { reference: ref, translation: secondId } })
      .then(({ data }) => {
        if (!cancelled) setSecond(data.ok ? data : null);
      })
      .catch(() => !cancelled && setSecond(null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [secondId, ref]);

  const nameFor = (id) => translations.find((t) => t.id === id)?.short || id.toUpperCase();

  const Column = ({ label, verses }) => (
    <div className="flex-1 min-w-0">
      <div className="text-[11px] font-medium uppercase tracking-widest text-[#00E5FF] mb-2">{label}</div>
      <div className="text-base leading-[1.85] text-slate-200 space-y-1">
        {(verses || []).map((v, i) => (
          <span key={i} className="inline">
            <sup className="text-[#00E5FF] text-[10px] font-sans font-medium mr-1 align-super">{v.verse}</sup>
            {v.text}{" "}
          </span>
        ))}
      </div>
    </div>
  );

  return (
    <div data-testid="compare-versions" className="mt-4 rounded-2xl bg-[#141B29]/70 border border-[#1E273D] p-5 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="text-xs font-medium uppercase tracking-widest text-slate-500">Compare versions</div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-500">Compare with</span>
          <select
            data-testid="compare-second-select"
            value={secondId}
            onChange={(e) => setSecondId(e.target.value)}
            className="h-8 rounded-lg border border-[#29344D] bg-[#0E131F] px-2 text-sm text-slate-300"
          >
            {translations
              .filter((t) => t.id !== current)
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.short} — {t.name}
                </option>
              ))}
          </select>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-6">
        <Column label={nameFor(current)} verses={passage.verses} />
        <div className="hidden sm:block w-px bg-[#1E273D] self-stretch" />
        <div className="flex-1 min-w-0">
          {loading ? (
            <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 text-[#00E5FF] animate-spin" /></div>
          ) : second ? (
            <Column label={second.translation_name || nameFor(secondId)} verses={second.verses} />
          ) : (
            <p className="text-sm text-slate-500">Couldn't load that version.</p>
          )}
        </div>
      </div>
    </div>
  );
}
